import { useCallback } from 'react'
import { Bandera } from '../componentes/Bandera.js'
import { Cargando, Problema, Vacio } from '../componentes/Estados.js'
import { Icono } from '../componentes/Icono.js'
import { MarcadorMatch } from '../componentes/MarcadorMatch.js'
import { MesaDelJugador } from '../componentes/MesaDelJugador.js'
import { MesasDeRonda } from '../componentes/MesasDeRonda.js'
import { RelojDeRonda } from '../componentes/RelojDeRonda.js'
import { PausaEntreRondas } from '../componentes/PausaEntreRondas.js'
import { MiPareja } from '../componentes/MiPareja.js'
import { MiPosicion } from '../componentes/MiPosicion.js'
import { AvisoExpulsion, MisSanciones, expulsionDe } from '../componentes/MisSanciones.js'
import { PastillaTorneo } from '../componentes/Pastilla.js'
import { TablaPosiciones } from '../componentes/TablaPosiciones.js'
import { useCarga } from '../carga.js'
import { obtenerRepositorio } from '../datos/index.js'
import type {
  MiMesa,
  MiTorneo,
  PosicionEquipoPublica,
  PosicionPublica,
  RondaPublica,
} from '../datos/index.js'
import { conSigno, cuantosCompiten, fechaLarga, plural, puesto, signoDe } from '../formato.js'
import { useAvisosDelTorneo, useLlamadaEspaciada } from '../tiempo-real.js'
import { ir, reemplazar } from '../navegacion.js'
import type { Pestana } from '../navegacion.js'

/**
 * Un torneo, mirado desde adentro.
 *
 * Cinco pestañas que son cinco preguntas distintas: qué es este torneo y cómo
 * va, dónde juego ahora, qué está pasando en las otras mesas, cómo va la tabla,
 * y qué me pasó en las rondas anteriores. Van en pestañas y no una debajo de la
 * otra porque en el teléfono la tabla de posiciones sola ya es más larga que la
 * pantalla, y apilarlas dejaría lo urgente (la mesa) arriba de un scroll de
 * cuatrocientas filas.
 *
 * El orden es el de la pregunta y no el de la urgencia: primero qué es esto,
 * después lo mío, después lo de los demás. Por cuál se ENTRA es otra decisión y
 * la toma la navegación: se entra por «Mi mesa», que es lo que se vino a buscar.
 *
 * Lo que cabe en una sola carga se pide junto: el torneo, mis mesas, la tabla y
 * la lista de rondas son cuatro consultas chicas, y pedirlas al cambiar de
 * pestaña haría que moverse entre ellas parpadee cada vez. Las mesas de las
 * otras rondas no entran ahí: son cien por ronda y se piden al mirarlas.
 */

const PESTANAS: readonly { id: Pestana; texto: string }[] = [
  { id: 'resumen', texto: 'Resumen' },
  { id: 'mesa', texto: 'Mi mesa' },
  { id: 'mesas', texto: 'Mesas' },
  { id: 'posiciones', texto: 'Posiciones' },
  { id: 'partidas', texto: 'Mis partidas' },
]

/** Cómo se llama cada modalidad para el que juega, no para el que la configura. */
const MODALIDAD: Record<string, string> = {
  individual: 'Individual',
  parejas: 'Parejas',
  equipos: 'Por equipos',
}

interface Datos {
  torneo: MiTorneo | null
  mesas: MiMesa[]
  posiciones: PosicionPublica[]
  /** La tabla de equipos. Vacía fuera de la modalidad Equipos. */
  equipos: PosicionEquipoPublica[]
  rondas: RondaPublica[]
}

export function Torneo({ torneoId, pestana }: { torneoId: string; pestana: Pestana }) {
  const pedir = useCallback(async (): Promise<Datos> => {
    const repo = await obtenerRepositorio()
    const torneo = await repo.obtenerTorneo(torneoId)
    if (!torneo) return { torneo: null, mesas: [], posiciones: [], equipos: [], rondas: [] }
    /* La de equipos se pide solo donde existe: en las otras modalidades sería un
       viaje de ida y vuelta para traer una lista vacía. */
    const [mesas, posiciones, equipos, rondas] = await Promise.all([
      repo.listarMisMesas(torneoId),
      repo.listarPosiciones(torneoId),
      torneo.modalidad === 'equipos' ? repo.listarPosicionesEquipos(torneoId) : [],
      repo.listarRondas(torneoId),
    ])
    return { torneo, mesas, posiciones, equipos, rondas }
  }, [torneoId])

  const { datos, error, cargando, recargar, refrescar } = useCarga(pedir)

  /* El torneo, sus rondas, la tabla y mis mesas se vuelven a pedir cuando llega
     un aviso que los cambia: una ronda armada o cerrada, el torneo entero, o
     algo de una de MIS mesas (el resultado de la que estoy jugando). Las manos
     de las mesas ajenas no: esas son de la pestaña «Mesas», que las pide sola. */
  const pronto = useLlamadaEspaciada(refrescar, 3_000)
  useAvisosDelTorneo(torneoId, false, (aviso) => {
    if (aviso.tipo === 'mesa' && !datos?.mesas.some((m) => m.mesaId === aviso.mesaId)) return
    pronto()
  })

  if (error) return <Problema mensaje={error} alReintentar={recargar} />
  if (!datos) return cargando ? <Cargando que="Abriendo el torneo…" /> : null

  const { torneo, mesas, posiciones, equipos, rondas } = datos

  if (!torneo) {
    return (
      <div className="pantalla">
        <Vacio titulo="Ese torneo no es tuyo">
          <p>
            O no existe, o no estás anotado en él. Acá solo se ven los torneos en los que jugás.
          </p>
          <button className="boton secundario" onClick={() => reemplazar({ nombre: 'torneos' })}>
            Ver mis torneos
          </button>
        </Vacio>
      </div>
    )
  }

  return (
    <div className="pantalla pantalla-torneo">
      {/*
        La cabecera se quedó con lo que identifica al torneo y nada más: el
        nombre, cuándo se juega, cuántos son y cuánto falta. Los números de la
        letra chica (a cuántos puntos, cuántas rondas, la modalidad) se mudaron
        al resumen. En el teléfono cada renglón de acá es un renglón que hay que
        pasar CADA VEZ que se entra a mirar la mesa, y eso se paga veinte veces
        por torneo.
      */}
      <header className="torneo-cabecera">
        <div className="torneo-cabecera-titulo">
          <h1>{torneo.nombre}</h1>
          <PastillaTorneo estado={torneo.estado} />
        </div>

        <p className="torneo-meta">
          <span>
            <Icono nombre="calendario" tamano={15} />
            {fechaLarga(torneo.fecha)}
          </span>
          {torneo.sede && (
            <span>
              <Icono nombre="sede" tamano={15} />
              {torneo.sede}
            </span>
          )}
          <span>
            <Icono nombre="gente" tamano={15} />
            {cuantosCompiten(torneo.participantes, torneo.modalidad)}
          </span>
        </p>

        {/* El match no tiene rondas que contar: tiene un marcador. */}
        {torneo.partidasAGanar !== null ? (
          <MarcadorMatch
            partidasAGanar={torneo.partidasAGanar}
            posiciones={posiciones}
            terminado={torneo.estado === 'finalizado'}
          />
        ) : (
          torneo.numeroRondas > 0 &&
          torneo.estado !== 'inscripcion' && <Avance torneo={torneo} />
        )}
      </header>

      {/* Entre la cabecera y las pestañas: la pausa entre rondas se ve desde
          cualquiera de ellas, que es lo que tiene que pasar con un aviso. */}
      <PausaEntreRondas torneo={torneo} />

      {/* Las pestañas van FUERA de la cabecera y son hermanas de ella, no hijas.
          Es lo que les permite quedarse pegadas arriba durante toda la pantalla:
          un `sticky` no pasa del borde de abajo de su contenedor, y adentro de
          la cabecera se despegarían cuatro renglones más abajo, justo cuando
          empieza la tabla de cuatrocientas filas por la que hay que bajar. */}
      <nav className="pestanas" aria-label="Secciones del torneo">
        {PESTANAS.map((p) => (
          <button
            key={p.id}
            className={pestana === p.id ? 'activa' : ''}
            aria-current={pestana === p.id ? 'page' : undefined}
            onClick={() => ir({ nombre: 'torneo', id: torneo.id, pestana: p.id })}
          >
            {p.texto}
          </button>
        ))}
      </nav>

      {pestana === 'resumen' && (
        <Resumen torneo={torneo} posiciones={posiciones} rondas={rondas} alRecargar={recargar} />
      )}

      {pestana === 'mesa' && <PestanaMesa torneo={torneo} mesas={mesas} />}

      {pestana === 'mesas' && (
        <MesasDeRonda
          torneoId={torneo.id}
          rondas={rondas}
          {...(torneo.cargaParcial !== 'desactivada'
            ? { alAnotar: (mesaId: string) => ir({ nombre: 'anotar', id: torneo.id, mesaId }) }
            : {})}
        />
      )}

      {pestana === 'posiciones' &&
        (posiciones.length === 0 ? (
          <Vacio titulo="Todavía no hay tabla">
            <p>La tabla se arma al iniciar el torneo, con los que hicieron check-in.</p>
          </Vacio>
        ) : (
          <TablaPosiciones
            posiciones={posiciones}
            equipos={equipos}
            hayPodio={torneo.rondasCerradas > 0}
          />
        ))}

      {pestana === 'partidas' && (
        <PestanaPartidas mesas={mesas} enJuego={torneo.estado === 'en_curso'} />
      )}
    </div>
  )
}

/** El semáforo de rondas: cuántas se cerraron, cuál se juega, cuántas faltan. */
export function Avance({
  torneo,
}: {
  torneo: Pick<MiTorneo, 'numeroRondas' | 'rondasCerradas' | 'rondaEnCurso'>
}) {
  const enCurso = torneo.rondaEnCurso

  return (
    <div className="avance-bloque">
      <div
        className="avance"
        role="img"
        aria-label={`${torneo.rondasCerradas} de ${torneo.numeroRondas} rondas cerradas`}
        title={`${torneo.rondasCerradas} de ${torneo.numeroRondas} rondas cerradas`}
      >
        {Array.from({ length: torneo.numeroRondas }, (_, i) => {
          const numero = i + 1
          const clase =
            numero <= torneo.rondasCerradas
              ? 'cerrada'
              : numero === enCurso
                ? 'en-curso'
                : 'pendiente'
          return <span key={numero} className={`tramo ${clase}`} />
        })}
      </div>
      {/* El semáforo dice cuánto falta de un vistazo, pero no dice el número, y
          el número es lo que se pregunta en voz alta entre dos rondas. */}
      <p className="avance-etiqueta tenue">
        {enCurso !== null
          ? `Ronda ${enCurso} de ${torneo.numeroRondas} en juego`
          : `${torneo.rondasCerradas} de ${torneo.numeroRondas} rondas cerradas`}
      </p>
    </div>
  )
}

/**
 * El torneo de un vistazo.
 *
 * Es la pestaña del que no está buscando su mesa: el que entra el día antes a
 * ver de qué se trata, el que entra al otro día a ver cómo terminó, el que sigue
 * desde afuera cómo va. Contesta en un scroll lo que antes había que juntar de
 * tres pantallas, y termina en los enlaces a esas tres.
 */
function Resumen({
  torneo,
  posiciones,
  rondas,
  alRecargar,
}: {
  torneo: MiTorneo
  posiciones: PosicionPublica[]
  rondas: RondaPublica[]
  /** Volver a pedir todo: durante la ronda los números cambian solos. */
  alRecargar: () => void
}) {
  const abierta = rondas.find((r) => r.estado !== 'cerrada' && r.mesas > 0) ?? null
  const arriba = posiciones.slice(0, 5)
  const hayPodio = torneo.rondasCerradas > 0

  return (
    <div className="resumen">
      {/* Primero que todo: el expulsado que entra al resumen viene a preguntar
          qué pasó, y la respuesta no puede estar debajo de «Ver mi mesa». */}
      <AvisoExpulsion sanciones={torneo.sanciones} />

      {torneo.estado === 'en_curso' && (
        <section className="seccion">
          <h3 className="seccion-titulo">Ahora mismo</h3>
          <div className="resumen-ahora">
            <p className="resumen-ahora-linea">
              <Icono nombre="reloj" tamano={17} />
              {abierta ? (
                <span>
                  Se juega la <b>ronda {abierta.numero}</b>: {abierta.mesasCargadas} de{' '}
                  {abierta.mesas} mesas cargadas.
                </span>
              ) : (
                <span>
                  La ronda {torneo.rondasCerradas} está cerrada y la próxima todavía no se armó.
                </span>
              )}
            </p>
            <div className="resumen-acciones">
              <button
                className="boton principal"
                onClick={() => ir({ nombre: 'torneo', id: torneo.id, pestana: 'mesa' })}
              >
                Ver mi mesa
              </button>
              <button
                className="boton secundario"
                onClick={() => ir({ nombre: 'torneo', id: torneo.id, pestana: 'mesas' })}
              >
                Ver todas las mesas
              </button>
              <button className="boton secundario" onClick={alRecargar}>
                <Icono nombre="refrescar" tamano={16} />
                Actualizar
              </button>
            </div>
          </div>
        </section>
      )}

      {/* Antes que la posición: en Parejas se juega con un nombre de a dos y un
          número, y eso es lo primero que se busca al abrir el torneo. Sale
          también el día antes, cuando todavía no hay puesto que mostrar. */}
      {torneo.pareja && (
        <section className="seccion">
          <MiPareja torneo={torneo} />
        </section>
      )}

      {torneo.estado === 'finalizado' ? (
        <section className="seccion">
          <h3 className="seccion-titulo">Cómo terminaste</h3>
          <Final torneo={torneo} />
        </section>
      ) : (
        torneo.mi && (
          <section className="seccion">
            <h3 className="seccion-titulo">Tu posición</h3>
            <MiPosicion torneo={torneo} />
          </section>
        )
      )}

      {torneo.sanciones.length > 0 && (
        <section className="seccion">
          <h3 className="seccion-titulo">Tus sanciones</h3>
          <MisSanciones sanciones={torneo.sanciones} />
        </section>
      )}

      {arriba.length > 0 && (
        <section className="seccion">
          <h3 className="seccion-titulo">
            {torneo.estado === 'finalizado' ? 'El podio' : 'Los que van adelante'}
          </h3>
          {/* Cinco y no la tabla entera: es el «¿y quién va primero?» que sigue
              siempre al «¿cómo voy?», y contestarlo con cuatrocientas filas es
              no contestarlo. La tabla completa está a un botón de acá. */}
          <ol className="resumen-podio">
            {arriba.map((p) => (
              <li key={p.participanteId} className={p.esMio ? 'mia' : ''}>
                <span className={`resumen-puesto ${hayPodio && p.posicion <= 3 ? 'podio' : ''}`}>
                  {p.posicion}
                </span>
                <Bandera codigo={p.pais} />
                <span className="acortar">{p.nombre}</span>
                {p.esMio && <span className="silla-tu">tú</span>}
                <span className="resumen-cifras">
                  <span className="ganadas">{p.ganadas}G</span>
                  <span className="perdidas">{p.perdidas}P</span>
                  <span className={`efectividad ${signoDe(p.efectividad)}`}>
                    {conSigno(p.efectividad)}
                  </span>
                </span>
              </li>
            ))}
          </ol>
          <button
            className="boton secundario"
            onClick={() => ir({ nombre: 'torneo', id: torneo.id, pestana: 'posiciones' })}
          >
            Ver la tabla completa
          </button>
        </section>
      )}

      <section className="seccion">
        <h3 className="seccion-titulo">El torneo</h3>
        {/* Los datos que no cambian en todo el día. Están acá y no en la
            cabecera justamente por eso: se leen una vez y después estorban. */}
        <dl className="resumen-datos">
          <div>
            <dt>Modalidad</dt>
            <dd>{MODALIDAD[torneo.modalidad] ?? torneo.modalidad}</dd>
          </div>
          <div>
            <dt>Se juega a</dt>
            <dd>{torneo.objetivoPuntos} puntos</dd>
          </div>
          {torneo.partidasAGanar !== null ? (
            <div>
              <dt>Partidas a ganar</dt>
              <dd>{torneo.partidasAGanar}</dd>
            </div>
          ) : (
            <div>
              <dt>Rondas</dt>
              <dd>{torneo.numeroRondas}</dd>
            </div>
          )}
          <div>
            <dt>
              {torneo.modalidad === 'parejas'
                ? 'Parejas'
                : torneo.estado === 'inscripcion'
                  ? 'Anotados'
                  : 'Participantes'}
            </dt>
            <dd>{torneo.participantes}</dd>
          </div>
        </dl>
      </section>
    </div>
  )
}

function PestanaMesa({ torneo, mesas }: { torneo: MiTorneo; mesas: MiMesa[] }) {
  if (torneo.estado === 'inscripcion' || torneo.estado === 'borrador') {
    return (
      <>
        <Vacio titulo="El torneo todavía no empezó">
          <p>
            Se juega el {fechaLarga(torneo.fecha)}. Acuérdate de hacer el check-in al llegar: recién
            ahí entrás al sorteo de la primera ronda.
          </p>
        </Vacio>
        {/* En Parejas la mesa que no existe todavía tiene igual una respuesta:
            con quién y con qué número se va a jugar. */}
        <MiPareja torneo={torneo} />
      </>
    )
  }

  const abierta = mesas.find((m) => m.rondaEstado !== 'cerrada')
  const ultima = mesas[mesas.length - 1]

  if (torneo.estado === 'finalizado') {
    return <Final torneo={torneo} />
  }

  /* El expulsado no tiene mesa que buscar: lo que se le muestra es por qué, y
     la última que jugó, que es la que tiene en la cabeza. */
  if (expulsionDe(torneo.sanciones)) {
    return (
      <>
        <AvisoExpulsion sanciones={torneo.sanciones} />
        {ultima && <MesaDelJugador mesa={ultima} />}
      </>
    )
  }

  if (!abierta) {
    return (
      <>
        <p className="nota">
          {ultima
            ? torneo.proximaRondaEn !== null
              ? `La ronda ${ultima.rondaNumero} está cerrada. La próxima se sortea al terminar la pausa.`
              : `La ronda ${ultima.rondaNumero} está cerrada y la próxima todavía no se armó.`
            : 'Todavía no se armó la primera ronda.'}
        </p>
        {ultima && <MesaDelJugador mesa={ultima} />}
        {ultima && <EnlaceAlSalon torneo={torneo} ronda={ultima.rondaNumero} />}
      </>
    )
  }

  /* La libreta no va debajo de la mesa: tiene pantalla propia, y la tarjeta
     tiene los botones para abrirla. Solo en los torneos que la usan, y no en
     la mesa sin rival, donde no hay partida. */
  const anotable = torneo.cargaParcial !== 'desactivada' && abierta.tipo === 'normal'

  return (
    <div className="columna-mayor">
      {/* Arriba de todo: con reloj, cuánto queda es lo primero que se mira. */}
      {abierta.venceEn !== null && (
        <RelojDeRonda venceEn={abierta.venceEn} ronda={abierta.rondaNumero} />
      )}
      <MesaDelJugador
        mesa={abierta}
        destacada
        {...(anotable
          ? { alAnotar: () => ir({ nombre: 'anotar', id: torneo.id, mesaId: abierta.mesaId }) }
          : {})}
      />
      <EnlaceAlSalon torneo={torneo} ronda={abierta.rondaNumero} />
    </div>
  )
}

/**
 * El paso de la propia mesa al resto del salón.
 *
 * Va debajo de la mesa y no arriba: el que abrió la pestaña vino a ver la suya,
 * y recién después de verla se pregunta por las otras. Es el camino a la
 * pestaña de mesas que aparece en el momento exacto en que se lo necesita.
 */
function EnlaceAlSalon({ torneo, ronda }: { torneo: MiTorneo; ronda: number }) {
  return (
    <button
      className="boton secundario"
      onClick={() => ir({ nombre: 'torneo', id: torneo.id, pestana: 'mesas' })}
    >
      Ver las otras mesas de la ronda {ronda}
    </button>
  )
}

/** Cómo terminó: el número con el que el torneo queda en la memoria. */
function Final({ torneo }: { torneo: MiTorneo }) {
  const mi = torneo.mi
  if (!mi) {
    return (
      <Vacio titulo="No llegaste a jugar este torneo">
        <p>Estabas en la nómina pero no entraste al sorteo, así que no tienes posición final.</p>
      </Vacio>
    )
  }

  const podio = mi.posicion <= 3

  return (
    <div className={`final ${podio ? 'podio' : ''}`}>
      {podio && <Icono nombre="medalla" tamano={30} />}
      <span className="final-puesto">{puesto(mi.posicion)}</span>
      <span className="final-de">de {torneo.participantes}</span>
      <dl className="mi-posicion-datos">
        <div>
          <dt>Ganadas</dt>
          <dd className="ganadas">{mi.ganadas}</dd>
        </div>
        <div>
          <dt>Perdidas</dt>
          <dd className="perdidas">{mi.perdidas}</dd>
        </div>
        <div>
          <dt>Efectividad</dt>
          <dd className={`efectividad ${signoDe(mi.efectividad)}`}>{conSigno(mi.efectividad)}</dd>
        </div>
        <div>
          <dt>Puntos</dt>
          <dd>{mi.puntosFavor}</dd>
        </div>
      </dl>
    </div>
  )
}

/**
 * Todas sus mesas del torneo, de la última a la primera.
 *
 * Al revés que la lista de rondas del organizador, que va en orden de juego. Acá
 * la pregunta es «¿cómo me fue recién?», y la respuesta está al final de la
 * lista: darla vuelta ahorra el scroll entero en un torneo de nueve rondas.
 */
function PestanaPartidas({ mesas, enJuego }: { mesas: MiMesa[]; enJuego: boolean }) {
  if (mesas.length === 0) {
    return (
      <Vacio titulo="Todavía no jugaste ninguna">
        <p>Cuando se cierre la primera ronda, tus partidas quedan acá con su marcador.</p>
      </Vacio>
    )
  }

  const jugadas = [...mesas].reverse()
  const cerradas = jugadas.filter((m) => m.marcador !== null)
  const ganadas = cerradas.filter((m) => m.marcador?.resultado === 'ganada').length
  const perdidas = cerradas.length - ganadas

  return (
    <>
      {/* El acumulado arriba de la lista: es la cuenta que el jugador hace solo
          mientras baja, y hacerla mal es la mitad de las discusiones del salón. */}
      {cerradas.length > 0 && (
        <p className="nota">
          Llevas <b>{plural(cerradas.length, 'partida jugada', 'partidas jugadas')}</b>: {ganadas}{' '}
          {ganadas === 1 ? 'ganada' : 'ganadas'} y {perdidas}{' '}
          {perdidas === 1 ? 'perdida' : 'perdidas'}.
          {enJuego && ' La que se está jugando todavía no cuenta.'}
        </p>
      )}
      <div className="partidas">
        {jugadas.map((m) => (
          <MesaDelJugador key={m.rondaId} mesa={m} />
        ))}
      </div>
    </>
  )
}
